import React, { useState, useEffect } from "react";

import {
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
} from "@mui/material";

import { OnboardingSelectorOptions } from "libLegacy/api/onboarding/onboardingModels";
import { fetchInvertersPlatform } from "libLegacy/api/onboarding/onboarding";

import { BlockStandard } from "components/admin/BlockStandard";
import { CustomSelect } from "components/onboarding/customSelect";
import { PasswordText } from "components/MUIExtensions/inputs/PasswordText";

import { useToken } from "contexts/UserContext";
import { useSetError } from "contexts/ErrorContext";

const FOTOVOLTAICO_OPTIONS = [
  { id: "sim", name: "Sim" },
  { id: "nao", name: "Não" },
];

export const SolarEnergyDetail = ({
  solarEnergyData,
  onChange,
}: SolarEnergyDetailProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [platforms, setPlatforms] =
    useState<Array<OnboardingSelectorOptions>>();
  const token = useToken();
  const setGlobalError = useSetError();

  useEffect(() => {
    const loadSelectData = async (token) => {
      try {
        const data = await fetchInvertersPlatform(token);
        setPlatforms(data);
      } catch (error) {
        // @ts-expect-error
        setGlobalError(error.response?.data?.error);
      } finally {
        setIsLoading(false);
      }
    };
    loadSelectData(token);
  }, [token, setGlobalError]);

  const handleFotovoltaicoChange = ({ target: { value } }) => {
    const hasPhotovoltaic =
      value === "sim" ? true : value === "nao" ? false : undefined;
    onChange({
      ...solarEnergyData,
      hasPhotovoltaic,
      // Reset other fields when switching to "Não"
      ...(value === "nao" && {
        platformId: 0,
        email: "",
        password: "",
      }),
    });
  };

  const handlePlatformChange = ({ target: { value } }) => {
    onChange({
      ...solarEnergyData,
      platformId: value,
    });
  };

  const handleEmailChange = ({ target: { value } }) => {
    onChange({
      ...solarEnergyData,
      email: value,
    });
  };

  const handlePasswordChange = (value: string) => {
    onChange({
      ...solarEnergyData,
      password: value,
    });
  };

  // Map boolean to select value
  const fotovoltaicoValue =
    solarEnergyData.hasPhotovoltaic === true
      ? "sim"
      : solarEnergyData.hasPhotovoltaic === false
      ? "nao"
      : "";

  return (
    <BlockStandard>
      <Typography variant="subtitle1">Energia solar</Typography>

      <Grid item container direction="column" gap={2}>
        <FormControl fullWidth>
          <InputLabel>Sistema Fotovoltaico</InputLabel>
          <Select
            value={fotovoltaicoValue}
            onChange={handleFotovoltaicoChange}
            label="Sistema Fotovoltaico"
          >
            {FOTOVOLTAICO_OPTIONS.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {solarEnergyData.hasPhotovoltaic === true &&
          !isLoading &&
          platforms && (
            <>
              <CustomSelect
                label="Plataforma de inversor"
                value={solarEnergyData.platformId}
                onChange={handlePlatformChange}
                options={platforms}
              />

              <TextField
                required
                fullWidth
                label="Seu login"
                variant="outlined"
                value={solarEnergyData.email}
                onChange={handleEmailChange}
              />

              <PasswordText
                label="Senha de acesso"
                value={solarEnergyData.password}
                onChange={handlePasswordChange}
              />
            </>
          )}
      </Grid>
    </BlockStandard>
  );
};

export type SolarEnergyDetailProps = {
  solarEnergyData: SolarEnergyDetailItem;
  onChange: (updated: SolarEnergyDetailItem) => void;
};

export type SolarEnergyDetailItem = {
  hasPhotovoltaic: boolean | undefined;
  platformId: number;
  email: string;
  password: string;
};

export const emptySolarEnergy: SolarEnergyDetailItem = {
  hasPhotovoltaic: undefined,
  platformId: 0,
  email: "",
  password: "",
};
